/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, DoCheck, Inject, Input, NgZone, OnChanges, SimpleChanges} from '@angular/core';
import {safeDetectChanges} from '../common/util/safe-detect-changes';
import * as _ from 'lodash';
import template from './creative-set-analysis-page.component.html';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

@Component({
  standalone: false,
  selector: 'adn-creative-set-analysis-page',
  template
})
export class CreativeSetAnalysisPageComponent implements DoCheck, OnChanges {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  activeTab: 'charts' | 'creatives' | 'tags' | 'co2' | 'reports' | 'traffic' | 'location' = 'charts';
  showingTraffic = false;
  showingLocation = false;
  canSeeReports = false;

  creatives: any[] = [];
  creativesEmpty = true;
  creativeCount = 0;
  selCreative: any = null;
  tagModel: {model: any} = {model: null};

  private lastModelId: string | undefined;
  private subscribedModelComms: any;

  constructor(
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true
      || LocalUserPermissions.getAllVisPermissions().AX_ADVERTISER;
  }

  get showNewMessage(): boolean {
    return !!this.effectiveIsNew;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
    if (changes.model) {
      this.tagModel = {model: this.model};
    }
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId !== this.lastModelId) {
      this.lastModelId = modelId;
      this.tagModel = {model: this.model};
      this.loadCreatives();
    }
  }

  selectTab(tab: CreativeSetAnalysisPageComponent['activeTab']): void {
    this.activeTab = tab;
    if (tab === 'traffic') {
      this.showingTraffic = true;
    } else if (tab === 'location') {
      this.showingLocation = true;
    }
  }

  private loadCreatives(): void {
    if (!this.model?.id || this.showNewMessage) {
      this.creatives = [];
      this.creativesEmpty = true;
      this.creativeCount = 0;
      this.selCreative = null;
      return;
    }
    this.Creative.query({creativeSet: this.model.id}).$promise.then((data: any) => {
      this.ngZone.run(() => {
        this.creatives = data.results || [];
        this.creativesEmpty = this.creatives.length === 0;
        this.creativeCount = this.creatives.length;
        this.selCreative = _.find(this.creatives, () => true) || null;
      });
    });
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNewFromComms: boolean) => {
      if (type) {
        return;
      }
      this.ngZone.run(() => {
        if (isNewFromComms === true || isNewFromComms === false) {
          this.effectiveIsNew = isNewFromComms;
        } else if (data?.name) {
          this.effectiveIsNew = false;
        }
        if (data) {
          if (this.model && data.name) {
            this.model.name = data.name;
          }
          this.tagModel = {model: this.model};
          this.loadCreatives();
        }
        safeDetectChanges(this.cdr);
      });
    });
  }
}
